import { useMutation, useQuery } from "@apollo/client";
import { STORE_ITEM_TYPE } from "enefel/store";
import { memo, useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { setPageTitle } from "../../helpers/browser";
import { BUY_ITEM, GET_USER_PURCHASES } from "../../query/StoreQueries";
import QueryError from "../Error/QueryError";
import { Toast } from "../Interface/Toast";
import Loading from "../Loading";
import { RootContext } from "../RootContext";
import { track } from "../../analytics";
import StoreAvatars from "./StoreAvatar";
import StoreBadges from "./StoreBadges";
import StoreDice from "./StoreDice";

const Wrapper = styled.div``;

const StoreShowWithUser = memo(({ user }) => {
  const { t } = useTranslation();

  const { setUser } = useContext(RootContext);

  useEffect(() => {
    track("store_viewed");
  }, []);
  // cache-and-network : en cache-first (le défaut), revenir sur la boutique par
  // navigation interne relit un cache qui pouvait ne jamais avoir été mis à
  // jour, et l'article acheté restait affiché jusqu'au rechargement complet.
  const { loading, error, data } = useQuery(GET_USER_PURCHASES, {
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-and-network",
  });
  const [buyItemStore, { loading: loadingBuy, error: errorBuy }] =
    useMutation(BUY_ITEM, {
      refetchQueries: [{ query: GET_USER_PURCHASES }],
    });

  // Le minuteur de disparition est porté par le Toast lui-même.
  // On retient le type acheté : un badge débloque une race à la création de
  // personnage, alors que les autres objets s'équipent depuis le profil.
  const [bought, setBought] = useState(null);
  const notifyBought = useCallback((itemType) => setBought(itemType), []);

  const handleBuyAvatar = (itemKey) => {
    // Garde sur la mutation, pas sur la requête : `loading` repasse à true à
    // chaque revalidation de la liste d'achats, et le clic était alors ignoré
    // sans le moindre retour à l'utilisateur.
    if (loadingBuy) {
      return;
    }
    buyItemStore({
      variables: {
        itemKey,
        type: STORE_ITEM_TYPE.AVATAR,
      },
    })
      .then(({ data }) => {
        track("item_purchased", { item_key: itemKey, item_type: STORE_ITEM_TYPE.AVATAR });
        setUser({ ...user, ...data.buyItemStore });
        notifyBought(STORE_ITEM_TYPE.AVATAR);
      })
      // errorBuy porte déjà l'affichage ; sans ce catch la promesse rejetée
      // remonterait en unhandled rejection.
      .catch(() => {});
  };
  const handleBuySeasonAvatar = (itemKey) => {
    // Garde sur la mutation, pas sur la requête : `loading` repasse à true à
    // chaque revalidation de la liste d'achats, et le clic était alors ignoré
    // sans le moindre retour à l'utilisateur.
    if (loadingBuy) {
      return;
    }
    buyItemStore({
      variables: {
        itemKey,
        type: STORE_ITEM_TYPE.SEASON_AVATAR,
      },
    })
      .then(({ data }) => {
        track("item_purchased", { item_key: itemKey, item_type: STORE_ITEM_TYPE.SEASON_AVATAR });
        setUser({ ...user, ...data.buyItemStore });
        notifyBought(STORE_ITEM_TYPE.SEASON_AVATAR);
      })
      // errorBuy porte déjà l'affichage ; sans ce catch la promesse rejetée
      // remonterait en unhandled rejection.
      .catch(() => {});
  };
  const handleBuy = (itemKey) => {
    // Garde sur la mutation, pas sur la requête : `loading` repasse à true à
    // chaque revalidation de la liste d'achats, et le clic était alors ignoré
    // sans le moindre retour à l'utilisateur.
    if (loadingBuy) {
      return;
    }
    buyItemStore({
      variables: {
        itemKey,
        type: STORE_ITEM_TYPE.BADGE,
      },
    })
      .then(({ data }) => {
        track("item_purchased", { item_key: itemKey, item_type: STORE_ITEM_TYPE.BADGE });
        setUser({ ...user, ...data.buyItemStore });
        notifyBought(STORE_ITEM_TYPE.BADGE);
      })
      // errorBuy porte déjà l'affichage ; sans ce catch la promesse rejetée
      // remonterait en unhandled rejection.
      .catch(() => {});
  };
  const handleBuyDice = (itemKey) => {
    // Garde sur la mutation, pas sur la requête : `loading` repasse à true à
    // chaque revalidation de la liste d'achats, et le clic était alors ignoré
    // sans le moindre retour à l'utilisateur.
    if (loadingBuy) {
      return;
    }
    buyItemStore({
      variables: {
        itemKey,
        type: STORE_ITEM_TYPE.DICE,
      },
    })
      .then(({ data }) => {
        track("item_purchased", { item_key: itemKey, item_type: STORE_ITEM_TYPE.DICE });
        setUser({ ...user, ...data.buyItemStore });
        notifyBought(STORE_ITEM_TYPE.DICE);
      })
      // errorBuy porte déjà l'affichage ; sans ce catch la promesse rejetée
      // remonterait en unhandled rejection.
      .catch(() => {});
  };

  setPageTitle(t("t:players", "__players"));
  // En cache-and-network, `loading` repasse à true à chaque revalidation :
  // n'afficher le loader que tant qu'on n'a rien à montrer, sinon la boutique
  // disparaîtrait à chaque retour sur la page.
  if (loading && !data) return <Loading />;
  return (
    <Wrapper>
      <h1>{t("t:Shop", "__Shop")}</h1>
      <br />
      {(error || errorBuy) && <QueryError error={error || errorBuy} />}
      {bought && (
        <Toast
          title={t("t:purchase title", "__purchase title")}
          message={
            bought === STORE_ITEM_TYPE.BADGE
              ? t("t:purchase confirmed badge", "__purchase confirmed badge")
              : t("t:purchase confirmed", "__purchase confirmed")
          }
          icon={bought === STORE_ITEM_TYPE.BADGE ? "🏟️" : "🎉"}
          onClose={() => setBought(null)}
        />
      )}

      <StoreBadges
        tooth={user ? user.tooth : 0}
        onBuy={handleBuy}
        loading={loadingBuy}
        purchases={data?.me?.purchases || []}
      />
      <br />
      <StoreDice
        tooth={user ? user.tooth : 0}
        onBuy={handleBuyDice}
        loading={loadingBuy}
        purchases={data?.me?.purchases || []}
      />
      <br />
      <StoreAvatars
        tooth={user ? user.tooth : 0}
        onBuy={handleBuyAvatar}
        onBuySeasonAvatar={handleBuySeasonAvatar}
        loading={loadingBuy}
        purchases={data?.me?.purchases || []}
      />
    </Wrapper>
  );
});

const StoreShow = memo(() => {
  const { user } = useContext(RootContext);

  return <StoreShowWithUser user={user} />;
});

export default StoreShow;
